// src/pages/Social.js
// Page principale Fynho Social : navigable des qu'on est connecte.
// Completer le profil social ouvre un formulaire directement ici (overlay),
// puisque la page Profil du site principal n'a pas ces champs.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import ROUTES from '../constants/routes';
import * as socialApi from '../utils/socialApi';
import CreateProfileModal from '../components/social/CreateProfileModal';
import SocialMap from '../components/social/SocialMap';
import EventCard from '../components/social/EventCard';
import ChatWindow from '../components/social/ChatWindow';

const DEFAULT_CITY = 'Douala';

function extractUserId(user) {
  if (!user) return null;
  return user.id ?? user.user_id ?? user._id ?? user.userId ?? null;
}

const Social = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, isAdminAuthenticated, user } = useAuth();
  const isAdmin = isAdminAuthenticated;
  const socialUserId = extractUserId(user);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileFetchFailed, setProfileFetchFailed] = useState(false);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [pendingCount, setPendingCount] = useState(0);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [chatTarget, setChatTarget] = useState(null);

  const pollRef = useRef(null);

  const canInteract = isAuthenticated && !!profile;

  const requireLogin = useCallback(() => {
    navigate(ROUTES.LOGIN);
  }, [navigate]);

  // Ouvre le formulaire de completion du profil directement ici (plus de redirection)
  const requireProfile = useCallback(() => {
    setShowCreateProfile(true);
  }, []);

  const requireAction = useCallback(() => {
    if (!isAuthenticated) requireLogin();
    else requireProfile();
  }, [isAuthenticated, requireLogin, requireProfile]);

  const loadProfile = useCallback(async () => {
    if (!isAuthenticated || !socialUserId) {
      setProfile(null);
      setProfileFetchFailed(false);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    try {
      const res = await socialApi.getProfile(socialUserId);
      if (res && Object.prototype.hasOwnProperty.call(res, 'profile')) {
        setProfile(res.profile || null);
        setProfileFetchFailed(false);
        if (res.profile) {
          socialApi.updatePresence(socialUserId, true).catch(() => {});
        }
      } else {
        setProfileFetchFailed(true);
      }
    } catch (err) {
      setProfileFetchFailed(true);
    } finally {
      setProfileLoading(false);
    }
  }, [isAuthenticated, socialUserId]);

  useEffect(() => {
    loadProfile();

    const handleUnload = () => {
      if (socialUserId) socialApi.updatePresence(socialUserId, false).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, socialUserId]);

  useEffect(() => {
    if (!profile) return;
    const poll = async () => {
      try {
        const res = await socialApi.listFriendRequests(socialUserId);
        setPendingCount(res.count || 0);
      } catch (err) {
        // silencieux
      }
    };
    poll();
    pollRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollRef.current);
  }, [profile, socialUserId]);

  const loadFriends = useCallback(async () => {
    const res = await socialApi.listFriends(socialUserId);
    setFriends(res.friends || []);
  }, [socialUserId]);

  const loadRequests = useCallback(async () => {
    const res = await socialApi.listFriendRequests(socialUserId);
    setRequests(res.requests || []);
    setPendingCount(res.count || 0);
  }, [socialUserId]);

  useEffect(() => {
    if (!profile) return;
    if (activeTab === 'friends') loadFriends();
    if (activeTab === 'invitations') loadRequests();
  }, [activeTab, profile, loadFriends, loadRequests]);

  const handleAcceptRequest = async (requestId) => {
    await socialApi.respondFriendRequest(requestId, 'accepted');
    loadRequests();
  };

  const handleRefuseRequest = async (requestId) => {
    await socialApi.respondFriendRequest(requestId, 'refused');
    loadRequests();
  };

  const handleRemoveFriend = async (friendId) => {
    await socialApi.removeFriend(socialUserId, friendId);
    loadFriends();
  };

  const handleBlockFriend = async (blockedId) => {
    await socialApi.blockUser(socialUserId, blockedId);
    loadFriends();
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const res = await socialApi.searchUser(value);
    setSearchResults(res.results || []);
  };

  const handleSendInvite = async (receiverId) => {
    if (!canInteract) return requireAction();
    await socialApi.sendFriendRequest(socialUserId, receiverId);
  };

  const handleOpenChat = (friendId, username) => {
    if (!canInteract) return requireAction();
    setChatTarget({ userId: friendId, username });
  };

  const styles = {
    page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg, paddingBottom: '80px' },
    hero: {
      position: 'relative', padding: '28px 20px 22px',
      background: `linear-gradient(160deg, rgba(255,215,0,0.10), transparent 70%)`,
      borderBottom: `1px solid ${theme.borderSoft}`,
      overflow: 'hidden',
    },
    heroTitle: { fontSize: '24px', fontWeight: '800', color: theme.textPrimary, letterSpacing: '-0.5px' },
    heroSubtitle: { fontSize: '13px', color: theme.textSecondary, marginTop: '6px', lineHeight: '1.5', maxWidth: '420px' },
    heroUserLine: { fontSize: '13px', color: theme.gold, fontWeight: '700', marginTop: '10px' },
    banner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 16px', backgroundColor: 'rgba(255,215,0,0.08)', borderBottom: `1px solid ${theme.borderSoft}` },
    bannerText: { fontSize: '12px', color: theme.textSecondary },
    bannerBtn: { padding: '8px 14px', backgroundColor: theme.gold, border: 'none', borderRadius: '8px', color: '#FFFFFF', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
    tabsRow: { display: 'flex', gap: '6px', padding: '10px 16px', overflowX: 'auto', scrollbarWidth: 'none', borderBottom: `1px solid ${theme.borderSoft}` },
    tab: { position: 'relative', display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', borderRadius: '10px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    tabActive: { backgroundColor: theme.gold, color: '#FFFFFF', border: `1px solid ${theme.gold}` },
    badge: { position: 'absolute', top: '-4px', right: '-4px', backgroundColor: '#F56565', color: '#FFFFFF', fontSize: '10px', fontWeight: '700', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    body: { flex: 1, padding: '16px' },
    friendRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: theme.bgCard, borderRadius: '10px', border: `1px solid ${theme.border}`, marginBottom: '8px' },
    avatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: theme.bgInput, objectFit: 'cover', flexShrink: 0 },
    onlineDot: { width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#48BB78', flexShrink: 0 },
    friendInfo: { flex: 1, minWidth: 0 },
    friendName: { fontSize: '13px', fontWeight: '600', color: theme.textPrimary },
    friendLoc: { fontSize: '11px', color: theme.textSecondary },
    smallBtn: { padding: '6px 10px', fontSize: '11px', fontWeight: '700', borderRadius: '7px', border: 'none', cursor: 'pointer' },
    searchInput: { width: '100%', padding: '12px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px', color: theme.textPrimary, fontSize: '14px', outline: 'none', marginBottom: '12px' },
    emptyState: { textAlign: 'center', padding: '32px', color: theme.textSecondary, fontSize: '13px' },
    prompt: { textAlign: 'center', padding: '48px 24px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' },
    promptText: { fontSize: '14px', color: theme.textSecondary, maxWidth: '260px' },
    promptBtn: { padding: '12px 24px', backgroundColor: theme.gold, border: 'none', borderRadius: '10px', color: '#FFFFFF', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  };

  const tabs = [
    { id: 'map', icon: '🗺️', label: 'Carte' },
    { id: 'events', icon: '🎉', label: 'Evenements' },
    { id: 'friends', icon: '👥', label: 'Amis' },
    { id: 'invitations', icon: '✉️', label: 'Invitations', badge: pendingCount },
    { id: 'search', icon: '🔍', label: 'Recherche' },
  ];

  if (profileLoading) {
    return <div style={{ ...styles.page, alignItems: 'center', justifyContent: 'center' }}>...</div>;
  }

  const cityForBrowsing = profile ? profile.city : DEFAULT_CITY;

  return (
    <div style={styles.page}>
      <Helmet><title>Fynho Social{profile ? ` | @${profile.username}` : ''}</title></Helmet>

      <div style={styles.hero}>
        <div style={styles.heroTitle}>Fynho Social</div>
        <div style={styles.heroSubtitle}>
          Decouvre les personnes et evenements autour de toi.
        </div>
        {profile && (
          <div style={styles.heroUserLine}>@{profile.username} · {profile.district}, {profile.city}</div>
        )}
      </div>

      {!isAuthenticated && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>Connecte-toi pour inviter, discuter et creer des evenements.</span>
          <button style={styles.bannerBtn} onClick={requireLogin}>Se connecter</button>
        </div>
      )}

      {isAuthenticated && profileFetchFailed && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>
            Impossible de verifier ton profil pour le moment (probleme de connexion au serveur).
          </span>
          <button style={styles.bannerBtn} onClick={loadProfile}>Reessayer</button>
        </div>
      )}

      {isAuthenticated && !profileFetchFailed && !profile && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>
            Complete ton profil (pseudo, quartier) pour apparaitre sur la carte et interagir.
          </span>
          <button style={styles.bannerBtn} onClick={requireProfile}>Completer mon profil</button>
        </div>
      )}

      <div style={styles.tabsRow} className="hide-scrollbar">
        {tabs.map((tabItem) => (
          <div
            key={tabItem.id}
            style={{ ...styles.tab, ...(activeTab === tabItem.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tabItem.id)}
          >
            <span>{tabItem.icon}</span>
            {tabItem.label}
            {tabItem.badge > 0 && <span style={styles.badge}>{tabItem.badge}</span>}
          </div>
        ))}
      </div>

      <div style={styles.body}>
        {activeTab === 'map' && (
          <SocialMap
            city={cityForBrowsing}
            userId={socialUserId}
            isAuthenticated={canInteract}
            onRequireLogin={requireAction}
          />
        )}

        {activeTab === 'events' && (
          <EventCard
            userId={socialUserId}
            city={cityForBrowsing}
            isAdmin={isAdmin}
            isAuthenticated={canInteract}
            onRequireLogin={requireAction}
          />
        )}

        {activeTab === 'friends' && (
          !isAuthenticated ? (
            <div style={styles.prompt}>
              <div style={styles.promptText}>Connecte-toi pour voir et gerer tes amis.</div>
              <button style={styles.promptBtn} onClick={requireLogin}>Se connecter</button>
            </div>
          ) : !profile ? (
            <div style={styles.prompt}>
              <div style={styles.promptText}>Complete ton profil social pour voir tes amis.</div>
              <button style={styles.promptBtn} onClick={requireProfile}>Completer mon profil</button>
            </div>
          ) : (
            <>
              {friends.length === 0 && <div style={styles.emptyState}>Aucun ami pour le moment. Utilise la recherche pour en trouver !</div>}
              {friends.map((friend) => (
                <div key={friend.user_id} style={styles.friendRow}>
                  <img src={friend.avatar || '/images/avatar-placeholder.png'} alt={friend.username} style={styles.avatar} />
                  <div style={styles.friendInfo}>
                    <div style={styles.friendName}>
                      {friend.username} {friend.is_online ? <span style={styles.onlineDot} /> : null}
                    </div>
                    <div style={styles.friendLoc}>{friend.district}, {friend.city}</div>
                  </div>
                  <button style={{ ...styles.smallBtn, backgroundColor: theme.gold, color: '#FFFFFF' }} onClick={() => handleOpenChat(friend.user_id, friend.username)}>Message</button>
                  <button style={{ ...styles.smallBtn, backgroundColor: theme.bgInput, color: theme.textSecondary }} onClick={() => handleRemoveFriend(friend.user_id)}>Retirer</button>
                  <button style={{ ...styles.smallBtn, backgroundColor: '#F56565', color: '#FFFFFF' }} onClick={() => handleBlockFriend(friend.user_id)}>Bloquer</button>
                </div>
              ))}
            </>
          )
        )}

        {activeTab === 'invitations' && (
          !isAuthenticated ? (
            <div style={styles.prompt}>
              <div style={styles.promptText}>Connecte-toi pour voir tes invitations.</div>
              <button style={styles.promptBtn} onClick={requireLogin}>Se connecter</button>
            </div>
          ) : !profile ? (
            <div style={styles.prompt}>
              <div style={styles.promptText}>Complete ton profil social pour voir tes invitations.</div>
              <button style={styles.promptBtn} onClick={requireProfile}>Completer mon profil</button>
            </div>
          ) : (
            <>
              {requests.length === 0 && <div style={styles.emptyState}>Aucune invitation en attente.</div>}
              {requests.map((req) => (
                <div key={req.id} style={styles.friendRow}>
                  <img src={req.avatar || '/images/avatar-placeholder.png'} alt={req.username} style={styles.avatar} />
                  <div style={styles.friendInfo}>
                    <div style={styles.friendName}>{req.username}</div>
                    <div style={styles.friendLoc}>{req.district}, {req.city}</div>
                  </div>
                  <button style={{ ...styles.smallBtn, backgroundColor: theme.gold, color: '#FFFFFF' }} onClick={() => handleAcceptRequest(req.id)}>Accepter</button>
                  <button style={{ ...styles.smallBtn, backgroundColor: theme.bgInput, color: theme.textSecondary }} onClick={() => handleRefuseRequest(req.id)}>Refuser</button>
                </div>
              ))}
            </>
          )
        )}

        {activeTab === 'search' && (
          <>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Rechercher un @username..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchResults.map((resultUser) => (
              <div key={resultUser.user_id} style={styles.friendRow}>
                <img src={resultUser.avatar || '/images/avatar-placeholder.png'} alt={resultUser.username} style={styles.avatar} />
                <div style={styles.friendInfo}>
                  <div style={styles.friendName}>
                    {resultUser.username} {resultUser.is_online ? <span style={styles.onlineDot} /> : null}
                  </div>
                  <div style={styles.friendLoc}>{resultUser.district}, {resultUser.city}</div>
                </div>
                <button style={{ ...styles.smallBtn, backgroundColor: theme.gold, color: '#FFFFFF' }} onClick={() => handleSendInvite(resultUser.user_id)}>Inviter</button>
              </div>
            ))}
          </>
        )}
      </div>

      {showCreateProfile && (
        <CreateProfileModal
          userId={socialUserId}
          onClose={() => setShowCreateProfile(false)}
          onCreated={(newProfile) => {
            setProfile(newProfile);
            setShowCreateProfile(false);
          }}
        />
      )}

      {chatTarget && (
        <ChatWindow
          userId={socialUserId}
          friendId={chatTarget.userId}
          friendUsername={chatTarget.username}
          onClose={() => setChatTarget(null)}
        />
      )}
    </div>
  );
};

export default Social;